import { periodLabel } from '@/lib/format';
import { droppedFolder } from '@/lib/dropped';
import { useCan } from '@/hooks/use-scopes';
import { useMemo, useState } from 'react';
import { useParams, Link, Navigate, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ArrowLeft, FileEdit, Eye, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { Card, PageHeader } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import {
  qk, fetchStrategy, fetchCreatives, createCreative,
  fetchStrategyActivity, type CreativeCreateBody, type Creative, fetchClient
} from '@/lib/queries';
import { uploadAsset } from '@/lib/upload';
import ActivityFeed from '@/components/activity/ActivityFeed';
import CreativeFormDialog from '@/components/creative/CreativeFormDialog';
import DoctorCard from '@/components/strategy/DoctorCard';
import AnalyticsSection from '@/components/analytics/AnalyticsSection';

export default function StrategyDetailPage() {
  const { id = '' } = useParams<{ id: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();

  const strategy = useQuery({
    queryKey: qk.strategy(id),
    queryFn: () => fetchStrategy(id),
    enabled: !!id,
  });
  const creatives = useQuery({
    queryKey: qk.creativesFiltered({ strategy_id: id }),
    queryFn: () => fetchCreatives({ strategy_id: id }),
    enabled: !!id,
  });

  const [briefSlot, setBriefSlot] = useState<{ slot_id: string; funnel: string; type: string } | null>(null);
  const canBrief = useCan('creatives.write');
  // the doctor this strategy is for: the planners pick them and keep their profile current
  const canPlan = useCan('strategies.write') || canBrief;
  const clientRow = useQuery({ queryKey: qk.client(strategy.data?.client_id ?? ''), queryFn: () => fetchClient(strategy.data!.client_id),
    enabled: !!strategy.data?.client_id && canPlan });
  const [adhocOpen, setAdhocOpen] = useState(false);

  const creativesByslot = useMemo(() => {
    const m = new Map<string, Creative>();
    for (const c of creatives.data ?? []) if (c.slot_id) m.set(c.slot_id, c);
    return m;
  }, [creatives.data]);
  // Safety net: a piece of this strategy that sits on no slot of it is still listed.
  const offPlan = useMemo(() => {
    const ids = new Set((strategy.data?.slots ?? []).map((sl) => sl.id));
    return (creatives.data ?? []).filter((c) => !c.slot_id || !ids.has(c.slot_id));
  }, [creatives.data, strategy.data]);

  const create = useMutation({
    mutationFn: async ({ body, refs }: { body: CreativeCreateBody; refs: File[] }) => {
      const c = await createCreative(body);
      if (refs.length) {
        for (const f of refs) {
          try { await uploadAsset({ creativeId: c.id, file: f, kind: 'reference', category: droppedFolder(f) ?? undefined }); }
          catch (e) { console.error('ref upload failed', f.name, e); }
        }
      }
      return c;
    },
    onSuccess: (c) => {
      toast.success(c.status === 'in_progress' ? `Submitted ${c.code}` : `Saved draft ${c.code}`);
      qc.invalidateQueries({ queryKey: qk.creativesFiltered({ strategy_id: id }) });
      qc.invalidateQueries({ queryKey: qk.creatives });
      qc.invalidateQueries({ queryKey: qk.strategy(id) });
      qc.invalidateQueries({ queryKey: qk.strategyActivity(id) });
      setBriefSlot(null);
      setAdhocOpen(false);
      navigate(`/creatives/${c.id}`);
    },
    onError: (err: unknown) => {
      const msg = err instanceof Error ? err.message : 'Create failed';
      toast.error(msg);
    },
  });

  if (!id) return <Navigate to="/strategies" replace />;
  if (strategy.isLoading) return <div className="text-muted text-sm">Loading...</div>;
  if (!strategy.data) return <Navigate to="/strategies" replace />;
  const s = strategy.data;

  return (
    <div>
      <Link to="/strategies" className="inline-flex items-center gap-1 text-xs text-muted hover:text-accent-400 mb-3">
        <ArrowLeft size={12} /> Back to strategies
      </Link>
      <PageHeader
        title={s.title}
        subtitle={`${s.code} · ${s.client_name} · ${s.status}`}
        actions={
          <Button icon={<Plus size={14} />} variant="secondary" onClick={() => setAdhocOpen(true)}>
            Extra creative
          </Button>
        }
      />

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <div className="text-xs text-muted mb-1">Funnel Mix</div>
          <div className="text-sm text-muted-strong">
            TOF {s.funnel_mix.TOF} · MOF {s.funnel_mix.MOF} · BOF {s.funnel_mix.BOF}
          </div>
        </Card>
        <Card>
          <div className="text-xs text-muted mb-1">Period</div>
          <div className="text-sm text-muted-strong">{periodLabel(s.period_start, s.period_end)}</div>
        </Card>
        <Card>
          <div className="text-xs text-muted mb-1">Created</div>
          <div className="text-sm text-muted-strong">{creatives.data?.length ?? 0} of {s.slots.length} slots</div>
        </Card>
      </div>

      <Card className="mb-5">
        <h2 className="font-medium text-muted-strong mb-3">Creative slots</h2>
        {s.slots.length === 0 ? (
          <div className="text-sm text-muted py-6 text-center">No slots seeded yet.</div>
        ) : (
          <ul className="divide-y divide-slate-800 text-sm">
            {s.slots.map((slot, i) => {
              const c = creativesByslot.get(slot.id);
              return (
                <li key={slot.id} className="py-2.5 flex items-center justify-between gap-3">
                  {/* phone: the title gets its own line instead of being cut to a few letters */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                    <span className="text-[10px] text-muted w-12 shrink-0">Slot {i + 1}</span>
                    <span className="text-xs text-muted whitespace-nowrap">{slot.funnel} · {slot.type}</span>
                    {slot.extra && <span className="text-[10px] px-1.5 py-0.5 rounded border border-slate-700 text-muted" title="Added after the plan">Extra</span>}
                    <span className="text-muted-strong basis-full sm:basis-auto min-w-0 break-words sm:truncate">{c?.title ?? slot.working_title ?? <span className="text-muted">(unbriefed)</span>}</span>
                  </div>
                  {c ? (
                    <Link to={`/creatives/${c.id}`} className="shrink-0">
                      <Button size="sm" variant="ghost" icon={<Eye size={12} />}>{c.code}</Button>
                    </Link>
                  ) : canBrief ? (
                    <Button size="sm" icon={<FileEdit size={12} />} onClick={() => setBriefSlot({ slot_id: slot.id, funnel: slot.funnel, type: slot.type })}>
                      Create
                    </Button>
                  ) : (
                    <span className="text-[11px] text-muted">unbriefed</span>
                  )}
                </li>
              );
            })}
          </ul>
        )}
        {offPlan.length > 0 && (
          <div className="mt-4">
            <div className="text-[10px] uppercase tracking-wide text-muted mb-1">Not on a slot</div>
            <ul className="divide-y divide-slate-800 text-sm">
              {offPlan.map((c) => (
                <li key={c.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 min-w-0">
                    <span className="text-xs text-muted whitespace-nowrap">{c.funnel_stage} · {c.creative_type}</span>
                    <span className="text-muted-strong basis-full sm:basis-auto min-w-0 break-words sm:truncate">{c.title}</span>
                  </div>
                  <Link to={`/creatives/${c.id}`} className="shrink-0">
                    <Button size="sm" variant="ghost" icon={<Eye size={12} />}>{c.code}</Button>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </Card>

      <DoctorCard s={s} canEdit={canPlan} talents={(clientRow.data?.talents ?? []).filter((t) => !!t.id) as Array<{ id: string; name: string }>} />

      {s.description && (
        <Card className="mb-5">
          <div className="text-xs text-muted mb-1">Description</div>
          <p className="text-sm text-muted-strong whitespace-pre-wrap">{s.description}</p>
        </Card>
      )}

      <AnalyticsSection scope="strategy" id={id} />

      <ActivityFeed
        queryKey={qk.strategyActivity(id)}
        fetcher={() => fetchStrategyActivity(id)}
        storageKey="activityFeed.strategy.open"
        emptyHint="No activity yet on this strategy."
      />

      {briefSlot && (
        <CreativeFormDialog
          mode="brief"
          strategyId={id}
          slot={briefSlot}
          onClose={() => setBriefSlot(null)}
          onSubmit={(body, refs) => create.mutate({ body, refs })}
          submitting={create.isPending}
        />
      )}
      {adhocOpen && (
        <CreativeFormDialog
          mode="adhoc"
          defaultStrategyId={id}
          onClose={() => setAdhocOpen(false)}
          onSubmit={(body, refs) => create.mutate({ body, refs })}
          submitting={create.isPending}
        />
      )}
    </div>
  );
}

